/**
 * SS Primitives — Button, Badge, Field (Input / Select / Textarea / Label)
 * ─────────────────────────────────────────────────────────────────────────────
 * Usage:
 *   import { Button, Badge, Field, Input, Select, Textarea } from './primitives';
 */
import React from 'react';
import { BADGE_COLORS, BUTTON_VARIANTS, baseInputStyle, labelStyle, FONT, RADIUS, COLOR } from './tokens';
import { SmartDateInput } from './SmartDateInput';

// ── Button ────────────────────────────────────────────────────────────────────
/**
 * @param {'primary'|'danger'|'success'|'amber'|'teal'|'ghost'|'ghostBlue'|'ghostRed'} variant
 * @param {'sm'|'md'} size
 */
export function Button({
  variant = 'primary',
  size    = 'md',
  disabled,
  onClick,
  type    = 'button',
  children,
  style: extraStyle,
  title,
}) {
  const base = BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary;
  const pad  = size === 'sm' ? '5px 11px' : '8px 16px';
  const fs   = size === 'sm' ? FONT.size.sm : FONT.size.base;

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      title={title}
      style={{
        ...base,
        padding:      pad,
        borderRadius: 10,
        fontSize:     fs,
        fontFamily:   FONT.family,
        fontWeight:   FONT.weight.semi,
        cursor:       disabled ? 'not-allowed' : 'pointer',
        opacity:      disabled ? 0.55 : 1,
        display:      'inline-flex',
        alignItems:   'center',
        gap:          5,
        whiteSpace:   'nowrap',
        transition:   'opacity .15s, filter .15s ease, transform .15s ease, box-shadow .15s ease',
        ...extraStyle,
      }}
      onMouseEnter={e => {
        if (disabled) return;
        e.currentTarget.style.filter = 'brightness(1.06)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.filter = '';
        e.currentTarget.style.transform = '';
      }}
    >
      {children}
    </button>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────
/**
 * @param {'blue'|'green'|'red'|'amber'|'purple'|'teal'|'gray'} color
 */
export function Badge({ color = 'gray', children, style: extraStyle }) {
  const palette = BADGE_COLORS[color] || BADGE_COLORS.gray;
  return (
    <span style={{
      ...palette,
      fontSize:     FONT.size.xs,
      fontWeight:   FONT.weight.semi,
      padding:      '2px 8px',
      borderRadius: RADIUS.pill,
      display:      'inline-block',
      whiteSpace:   'nowrap',
      letterSpacing: '0.2px',
      ...extraStyle,
    }}>
      {children}
    </span>
  );
}

// ── Label ─────────────────────────────────────────────────────────────────────
export function Label({ children, required, htmlFor }) {
  return (
    <label htmlFor={htmlFor} style={labelStyle}>
      {children}
      {required && <span style={{ color: COLOR.red, marginLeft: 2 }}>*</span>}
    </label>
  );
}

// ── Input ─────────────────────────────────────────────────────────────────────
// When type="date", render SmartDateInput instead of the native browser picker.
// The SmartDateInput emits onChange({ target: { value: 'YYYY-MM-DD' } }) so all
// existing handlers work without any changes at the call site.
export const Input = React.forwardRef(function Input({ style: extra, type, ...props }, ref) {
  if (type === 'date') {
    // SmartDateInput doesn't use a ref in the same way; pass through style + all props.
    return <SmartDateInput style={extra} {...props} />;
  }
  return (
    <input
      ref={ref}
      type={type}
      style={{ ...baseInputStyle, ...extra }}
      {...props}
    />
  );
});

// ── Select ────────────────────────────────────────────────────────────────────
export const Select = React.forwardRef(function Select({ style: extra, children, ...props }, ref) {
  return (
    <select
      ref={ref}
      style={{ ...baseInputStyle, ...extra }}
      {...props}
    >
      {children}
    </select>
  );
});

// ── Textarea ──────────────────────────────────────────────────────────────────
export const Textarea = React.forwardRef(function Textarea({ style: extra, rows = 3, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      style={{ ...baseInputStyle, resize: 'vertical', ...extra }}
      {...props}
    />
  );
});

// ── Field ─────────────────────────────────────────────────────────────────────
/**
 * Wraps any form control with a label and optional bottom margin.
 *
 * <Field label="Username" required>
 *   <Input value={...} onChange={...} />
 * </Field>
 */
export function Field({ label, required, htmlFor, children, style: extra }) {
  return (
    <div style={{ marginBottom: 14, ...extra }}>
      {label && <Label required={required} htmlFor={htmlFor}>{label}</Label>}
      {children}
    </div>
  );
}
